import { useCallback, useMemo } from 'react';
import { toast } from 'react-toastify';

import { useBudgetMutation } from 'utils/api/mutations';
import { useBudgetQueries } from 'utils/api/queries';
import { toastStyle } from 'utils/helper';

export const useFilmIncentives = (budgetId, enabled = true) => {
  const {
    data: queryData,
    isLoading: isFetching,
    refetch,
  } = useBudgetQueries.useFilmIncentivesQuery({
    enabled: enabled && Boolean(budgetId),
    budgetId,
  });
  const createMutation = useBudgetMutation.useCreateFilmIncentiveMutation();
  const updateMutation = useBudgetMutation.useUpdateFilmIncentiveMutation();
  const deleteMutation = useBudgetMutation.useDeleteFilmIncentiveMutation();

  const incentives = useMemo(() => {
    const rows = queryData?.data || [];
    return [...rows].sort(
      (a, b) =>
        (a.position || 0) - (b.position || 0) || (a.id || 0) - (b.id || 0)
    );
  }, [queryData]);

  const saveIncentive = useCallback(
    async payload => {
      try {
        const result = payload.id
          ? await updateMutation.mutateAsync(payload)
          : await createMutation.mutateAsync(payload);
        await refetch();
        return result;
      } catch (error) {
        toast.error('Failed to save film tax credit estimate', toastStyle);
        throw error;
      }
    },
    [createMutation, refetch, updateMutation]
  );

  const deleteIncentive = useCallback(
    async id => {
      try {
        await deleteMutation.mutateAsync(id);
        await refetch();
      } catch (error) {
        toast.error('Failed to delete film tax credit estimate', toastStyle);
        throw error;
      }
    },
    [deleteMutation, refetch]
  );

  const isSaving =
    createMutation.isLoading ||
    createMutation.isPending ||
    updateMutation.isLoading ||
    updateMutation.isPending ||
    deleteMutation.isLoading ||
    deleteMutation.isPending;

  return {
    incentives,
    isFetching,
    isLoading: isSaving,
    isSaving,
    saveIncentive,
    deleteIncentive,
    refetch,
  };
};
